import { deriveReelSections } from '@src/services/reel/deriveReelSections';
import { extractErrorInfo } from '@src/utility/error';

import { VideoEditorLogger } from '../../../utility/VideoEditorLogger';
import { TranscriptTrack } from '../../models/TranscriptTrack';
import { IdGenerator } from '../../services/IdGenerator';
import { EditorChangeType } from '../../types';
import { AbstractCommand } from '../AbstractCommand';
import { applyReelSectionOrder } from './applyReelSectionOrder';
import { buildReelPortion } from './reelPortion';

import type { Scene } from '../../models/layout/Scene';
import type { VideoEditor } from '../../VideoEditor';
import type { TimeRange } from '../lib/snapRangesToSceneBoundaries';
import type { ReelSectionOrderEntry } from './applyReelSectionOrder';

/**
 * PLAT-11057 — adds clips to a reel that already exists, at a chosen Section
 * boundary.
 *
 * Like every other reel gesture this is a **timeline** operation followed by a
 * re-projection (DE3): the ranges become new transcript elements, the track is
 * re-laid out, and the transcript is rebuilt from the original. What the
 * creator already edited — trims, removed words, graphics — lives on the
 * existing elements, which this command only moves in time.
 *
 * Each range becomes exactly one Section (DE5/DE15), never a split of an
 * existing one: `atIndex` is a boundary *between* Sections, `0` putting the new
 * clips in front of the reel and `sections.length` at its end.
 *
 * The portions are built from the source media rather than from the picked
 * clip's own `videoSource` (DS4) — a saved clip's timeline is its own cropped
 * coordinate space, and what this reel needs is the passage. Every element in
 * the reel points at the same source, so an existing one is the template.
 *
 * Undo restores the pre-command timeline snapshot taken by
 * {@link AbstractCommand}.
 */
export class InsertReelSectionsCommand extends AbstractCommand {
  private ranges: TimeRange[];
  private atIndex: number;
  /** Reel-level elements (template intros / end cards) exempt from carry (DE7). */
  private exemptElementIds: ReadonlySet<string> | undefined;

  private executed = false;
  private originalTranscriptTrackId: string | undefined;
  private originalTimelineDuration: number | null = null;
  /**
   * The scenes as they stood before the re-layout re-anchored them.
   * {@link TimelineSnapshot} carries tracks and elements but not scenes, so the
   * same reason {@link RemoveReelSectionCommand} keeps its own copy applies
   * here: without it an undo puts the old element times back underneath the
   * new anchors.
   */
  private previousScenes: Scene[] | undefined;

  /** Provenance ids of the Sections this command created, in insertion order. */
  readonly insertedSectionIds: string[] = [];

  constructor(
    editor: VideoEditor,
    ranges: TimeRange[],
    atIndex: number,
    exemptElementIds?: ReadonlySet<string>,
  ) {
    super(editor);
    this.ranges = ranges;
    this.atIndex = atIndex;
    this.exemptElementIds = exemptElementIds;
  }

  getName(): string {
    return 'InsertReelSectionsCommand';
  }

  getDescription(): string {
    return `Insert ${this.ranges.length} reel section(s) at index ${this.atIndex}`;
  }

  execute(): boolean {
    if (this.executed) return false;

    if (this.ranges.length === 0) {
      VideoEditorLogger.logCommand(this, 'error', { error: 'no_ranges' });
      return false;
    }

    const timeline = this.editor.timeline;
    const track = timeline.tracks.find(
      (t): t is TranscriptTrack => t instanceof TranscriptTrack,
    );
    if (!track) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'no_transcript_track',
      });
      return false;
    }

    // Every portion is a clone of an element already in the reel, so an empty
    // track has nothing to build from — and a reel always has one Section
    // (DE15), so this is a guard rather than a case.
    const template = [...track.elements].sort((a, b) => a.time - b.time)[0];
    if (!template) {
      VideoEditorLogger.logCommand(this, 'error', { error: 'empty_track' });
      return false;
    }

    if (!this.editor.getOriginalTranscript()) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'no_original_transcript',
      });
      return false;
    }

    const sections = deriveReelSections(timeline);
    const atIndex = Math.max(0, Math.min(this.atIndex, sections.length));

    super.execute(); // captures the timeline snapshot for undo

    try {
      VideoEditorLogger.logCommand(this, 'start', {
        rangeCount: this.ranges.length,
        atIndex,
        sectionCount: sections.length,
      });

      this.originalTranscriptTrackId = this.editor.getTranscriptTrack()?.id;
      this.originalTimelineDuration = timeline.duration ?? null;
      this.previousScenes = [...timeline.scenes];

      // One Section per range, snapped to frames so a portion lands where the
      // renderer can actually cut. `time` is a placeholder — the layout below
      // assigns every element's position, arriving ones included.
      const inserted = this.ranges.map(range => {
        const start = timeline.snapTimeToFrame(range.start);
        const end = timeline.snapTimeToFrame(range.end);
        const reelSectionId = IdGenerator.shortRandomId();
        this.insertedSectionIds.push(reelSectionId);
        return buildReelPortion(template, {
          time: 0,
          duration: Math.max(0, end - start),
          mediaStart: start,
          reelSectionId,
        });
      });

      // The order to play in: the existing Sections with the arriving ones
      // spliced in at the boundary. From here it is the same layout a reorder
      // runs — capture what each Section owns, rebuild the track, re-lay it out
      // contiguously, carry the owned elements, re-project (DE6).
      const order: ReelSectionOrderEntry[] = [...sections];
      order.splice(
        atIndex,
        0,
        ...inserted.map((element, index) => ({
          id: this.insertedSectionIds[index],
          elementIds: [element.id],
        })),
      );

      const { restored, duration } = applyReelSectionOrder(
        this.editor,
        track,
        sections,
        order,
        this.exemptElementIds,
        inserted,
      );

      // An insert makes the reel longer — so the end card
      // has to be re-derived against the new content end before the duration is
      // written
      this.recalculateTimeline();

      if (!restored) {
        VideoEditorLogger.logCommand(this, 'error', {
          error: 'restore_transcript_failed',
        });
      }

      this.editor.markDirty('insert_reel_sections');
      this.editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED, {
        type: 'reel_sections_inserted',
        atIndex,
        sectionIds: this.insertedSectionIds,
      });

      this.executed = true;
      VideoEditorLogger.logCommand(this, 'complete', {
        atIndex,
        sectionIds: this.insertedSectionIds,
        duration,
      });
      return true;
    } catch (error) {
      // PLAT-11191 — half a re-layout is worse than none. By the time anything
      // in here can throw the track may already have been rebuilt and re-laid
      // out, and returning `false` on top of that leaves the arrival on the reel
      // with no undo entry to take it back: `ReplaceReelSectionCommand` abandons
      // its removal on this signal, so the creator gets the passage they picked
      // *added* next to the one they meant to swap out, and no repaint — which
      // is what "Replace with Clip does nothing" was. Undoing to the snapshot
      // `super.execute()` took is the same restore `undo()` performs, so a
      // command that reports failure has changed nothing.
      this.restoreTimelineSnapshot();
      if (this.originalTranscriptTrackId) {
        this.editor.setTranscriptTrack(this.originalTranscriptTrackId);
      }
      if (this.originalTimelineDuration !== null) {
        this.editor.timeline.setDuration(this.originalTimelineDuration);
      }
      if (this.previousScenes) {
        this.editor.timeline.setScenes(this.previousScenes);
      }
      this.insertedSectionIds.length = 0;

      // The stack, not only the message: every guard above names the reason it
      // refused, so a bare message is the one failure here that says nothing
      // about where it came from.
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'insert_threw',
        ...extractErrorInfo(error),
      });
      return false;
    }
  }

  undo(): boolean {
    if (!this.executed) return false;

    const result = super.undo();

    // The transcript restore re-pointed transcriptTrackId at the rebuilt
    // track; the snapshot restores the tracks but not that id, nor the
    // duration the insert grew.
    if (result && this.originalTranscriptTrackId) {
      this.editor.setTranscriptTrack(this.originalTranscriptTrackId);
    }
    if (result && this.originalTimelineDuration !== null) {
      this.editor.timeline.setDuration(this.originalTimelineDuration);
    }
    if (result && this.previousScenes) {
      this.editor.timeline.setScenes(this.previousScenes);
    }

    VideoEditorLogger.logCommand(this, 'complete', {
      operation: 'undo',
      result,
    });

    return result;
  }
}
